<template>
    <div>
        <PageHeader :data="{
            name: 'Platform Fingerprints',
            breadcrumbs: [
                { name: 'Home', to: '/' }
            ]
        }" />

        <div class="row">
            <!-- Fingerprint Counts Chart -->
            <div class="col-12 mb-4">
                <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-center">
                        <div class="d-flex align-items-center">
                            <h5 class="mb-0 me-2">
                                {{ chartSelectedType ? chartSelectedType.charAt(0).toUpperCase() + chartSelectedType.slice(1) : 'All' }} Platform Distribution
                            </h5>
                            <small class="text-muted">Municipality count by platform</small>
                        </div>
                        <div class="d-flex align-items-center">
                            <div class="me-2">
                                <input 
                                    type="week" 
                                    class="form-control form-control-sm" 
                                    style="width: 120px; font-size: 12px;" 
                                    v-model="selectedWeek"
                                    @change="fetchWeeklyChecksForPatterns('date')"
                                    title="Select week"
                                >
                            </div>
                            <select class="form-select" style="width: 150px;" v-model="chartSelectedType" @change="fetchWeeklyChecksForPatterns('type')">
                                <option value="">All Types</option>
                                <option value="provider">Provider</option>
                                <option value="technology">Technology</option>
                                <option value="hosting">Hosting</option>
                                <option value="cms">CMS</option>
                                <option value="product">Product</option>
                            </select>
                        </div>
                    </div>
                    <div class="card-body">
                        <div v-if="chartLoading" class="d-flex justify-content-center">
                            <div class="spinner-border" role="status">
                                <span class="visually-hidden">Loading chart...</span>
                            </div>
                        </div>
                        <div v-else-if="fingerprintCountsChart.labels.length > 0">
                            <StatsBarChart 
                                :data="fingerprintCountsChart" 
                                :options="chartOptions" 
                                :height="300" 
                                class="mb-3"
                            />
                            <!-- Custom Legend with Logos -->
                            <div class="d-flex flex-wrap justify-content-center mt-3">
                                <div 
                                    v-for="(item, index) in chartDataItems" 
                                    :key="index"
                                    class="d-flex align-items-center me-3 mb-2 cursor-pointer"
                                    :class="{ 'opacity-50': hiddenDatasets.includes(index) }"
                                    @click="toggleDataset(index)"
                                    :title="hiddenDatasets.includes(index) ? 'Click to show' : 'Click to hide'"
                                >
                                    <div 
                                        class="me-2 rounded-circle"
                                        :style="{ 
                                            width: '16px', 
                                            height: '16px', 
                                            backgroundColor: item.color || '#4285f4',
                                            minWidth: '16px',
                                            flexShrink: 0
                                        }"
                                    ></div>
                                    <div 
                                        v-if="item.logo"
                                        class="me-2 rounded d-flex align-items-center justify-content-center"
                                        style="width: 50px; height: 50px; background-color: white; padding: 2px; flex-shrink: 0;"
                                    >
                                        <img 
                                            :src="item.logo.startsWith('http') ? item.logo : storageUrl + item.logo" 
                                            :alt="item.name + ' logo'"
                                            style="width: 100%; height: 100%; object-fit: contain;"
                                            @error="$event.target.style.display='none'"
                                        />
                                    </div>
                                    
                                    <!-- Count (always show) -->
                                    <small class="text-muted">
                                        <template v-if="item.logo">({{ item.count.toLocaleString() }})</template>
                                        <template v-else>{{ item.name }} ({{ item.count.toLocaleString() }})</template>
                                    </small>
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center text-muted">
                            <p>No data available for the selected type</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Patterns Card -->
            <div class="col-12 mb-4">
                <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-center">
                        <div>
                            <h5 class="mb-0">Platform Fingerprints</h5>
                            <small class="text-muted" v-if="totalTrackedCount > 0">
                                Total tracked client count: <strong>{{ totalTrackedCount.toLocaleString() }}</strong>
                            </small>
                        </div>
                        <div>
                            <select class="form-select me-2 d-inline-block w-auto" v-model="selectedType">
                                <option value="">All Types</option>
                                <option value="provider">Provider</option>
                                <option value="technology">Technology</option>
                                <option value="hosting">Hosting</option>
                                <option value="cms">CMS</option>
                                <option value="product">Product</option>
                            </select>
                            <button class="btn btn-primary" @click="openAddModal">
                                <font-awesome-icon :icon="['fas', 'plus']" /> Add Pattern
                            </button>
                        </div>
                    </div>
                    <div class="card-body">
                        <table :class="!loading && patterns && patterns.data && patterns.data.length > 0 ? 'table table-hover' : 'table table-hover loading'">
                            <thead>
                                <tr>
                                    <th @click="sortBy('name')" class="sortable-header" role="button">
                                        Name
                                        <i class="fas ms-2" :class="getSortIcon('name')"></i>
                                    </th>
                                    <th @click="sortBy('type')" class="sortable-header" role="button">
                                        Type
                                        <i class="fas ms-2" :class="getSortIcon('type')"></i>
                                    </th>
                                    <th @click="sortBy('website_count')" class="sortable-header" role="button">
                                        Count
                                        <i class="fas ms-2" :class="getSortIcon('website_count')"></i>
                                    </th>
                                    <th>Weekly Checks</th>
                                    <th>Patterns</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="pattern in sortedPatterns" :key="pattern.id">
                                    <td>
                                        <div class="d-flex align-items-center">
                                            <!-- Color indicator -->
                                            <div 
                                                class="me-2 rounded-circle" 
                                                :style="{ 
                                                    backgroundColor: pattern.color || '#4285f4', 
                                                    width: '12px', 
                                                    height: '12px',
                                                    minWidth: '12px'
                                                }"
                                            ></div>
                                            
                                            <!-- Logo -->
                                            <img 
                                                v-if="pattern.logo" 
                                                :src="pattern.logo.startsWith('http') ? pattern.logo : storageUrl + pattern.logo" 
                                                :alt="pattern.name + ' logo'"
                                                class="me-2"
                                                style="width: 40px; height: 40px; object-fit: contain;"
                                                @error="$event.target.style.display='none'"
                                            />
                                            
                                            <!-- Name link -->
                                            <router-link 
                                                :to="{ name: 'competitor', params: { id: pattern.id } }"
                                                class="btn btn-link cursor-pointer p-0 border-0 text-start"
                                                :title="'Click to view ' + pattern.type + ' changes for ' + pattern.name"
                                            >
                                                {{ pattern.name }}
                                            </router-link>
                                        </div>
                                    </td>
                                    <td><span class="badge bg-info">{{ pattern.type }}</span></td>
                                    <td>
                                        <router-link
                                            v-if="pattern.website_count != null"
                                            :to="{
                                                path: '/municipalities',
                                                query: { [pattern.type]: pattern.id }
                                            }"
                                            class="btn btn-link cursor-pointer p-0 border-0 text-start"
                                            style="cursor:pointer; text-decoration:underline;"
                                        >
                                            {{ pattern.website_count.toLocaleString() }}
                                        </router-link>
                                        <span v-else>-</span>
                                    </td>
                                    <td class="chart-cell">
                                        <div class="chart-container">
                                            <div v-if="weeklyChecksLoading" class="chart-loader">
                                                <div class="spinner-border spinner-border-sm" role="status">
                                                    <span class="visually-hidden">Loading charts...</span>
                                                </div>
                                            </div>
                                            <template v-else>
                                                <svg
                                                    :width="100"
                                                    :height="20"
                                                    class="trend-line"
                                                    @mousemove="pattern.weekly_checks && pattern.weekly_checks.length ? showTooltip($event, pattern.weekly_checks) : null"
                                                    @mouseleave="hideTooltip"
                                                >
                                                    <!-- X axis line only -->
                                                    <line x1="2" y1="18" x2="98" y2="18" stroke="#ccc" stroke-width="1.5" />
                                                    
                                                    <template v-if="pattern.weekly_checks && pattern.weekly_checks.length">
                                                        <rect
                                                            v-for="(check, index) in pattern.weekly_checks"
                                                            :key="index"
                                                            :x="2 + index * (92 / pattern.weekly_checks.length)"
                                                            :y="18 - getBarHeight(pattern.weekly_checks, check.count)"
                                                            :width="92 / pattern.weekly_checks.length - 1"
                                                            :height="getBarHeight(pattern.weekly_checks, check.count)"
                                                            :fill="getBarColor(pattern.weekly_checks, index)"
                                                        />
                                                    </template>
                                                    <text
                                                        v-if="!pattern.weekly_checks || !pattern.weekly_checks.length"
                                                        x="50"
                                                        y="10"
                                                        text-anchor="middle"
                                                        dominant-baseline="middle"
                                                        class="empty-chart-text"
                                                    >
                                                        No data
                                                    </text>
                                                </svg>
                                            </template>
                                        </div>
                                    </td>
                                    <td>
                                        <span v-for="(p, index) in pattern.patterns" :key="index"
                                            class="badge bg-secondary me-1">
                                            {{ p }}
                                        </span>
                                    </td>
                                    <td>
                                        <button class="btn btn-sm btn-primary me-2" @click="editPattern(pattern)">
                                            <font-awesome-icon :icon="['fas', 'edit']" />
                                        </button>
                                        <button class="btn btn-sm btn-danger" @click="deletePattern(pattern.id)">
                                            <font-awesome-icon :icon="['fas', 'trash']" />
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div v-if="!loading && patterns && patterns.data && patterns.data.length === 0" class="col-md-12 text-center">
                            <h4>No data available</h4>
                        </div>
                        <div v-if="loading && patterns && patterns.data && patterns.data.length === 0" class="d-flex justify-content-center">
                            <div class="spinner-border" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>

                        <!-- Pagination -->
                        <LPDataTablePagination 
                            v-if="patterns.data && patterns.data.length > 0" 
                            :pagination="patterns" 
                            v-on:getItems="changePage($event.page)" 
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- Pattern Modal -->
        <div class="modal fade" id="patternModal" tabindex="-1">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ editMode ? 'Edit Pattern' : 'Add Pattern' }}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <form @submit.prevent="savePattern">
                            <div class="mb-3">
                                <label class="form-label">Name</label>
                                <input type="text" class="form-control" v-model="form.name" required>
                            </div>
                            <div class="mb-3" v-if="!editMode">
                                <label class="form-label">Type</label>
                                <select class="form-select" v-model="form.type" required>
                                    <option value="provider">Provider</option>
                                    <option value="technology">Technology</option>
                                    <option value="hosting">Hosting</option>
                                    <option value="cms">CMS</option>
                                    <option value="product">Product</option>
                                </select>
                            </div>
                            <div class="mb-3">
                                <label class="form-label">Color</label>
                                <div class="d-flex align-items-center">
                                    <input type="color" class="form-control form-control-color me-2" v-model="form.color" style="width: 60px; height: 38px;">
                                    <input type="text" class="form-control" v-model="form.color" placeholder="#4285f4">
                                </div>
                            </div>
                            <div class="mb-3">
                                <label class="form-label">Logo</label>
                                <div v-if="form.logo && !uploadingLogo" class="mb-2">
                                    <small class="text-muted d-block mb-1">Current logo:</small>
                                    <img :src="form.logo.startsWith('http') ? form.logo : storageUrl + form.logo" alt="Logo preview" style="max-width: 50px; max-height: 50px; object-fit: contain;" 
                                         @error="$event.target.style.display='none'" />
                                </div>
                                <div>
                                    <input 
                                        ref="logoFileInput"
                                        type="file" 
                                        accept="image/*" 
                                        @change="uploadLogo($event)" 
                                        class="form-control"
                                        :disabled="uploadingLogo"
                                    >
                                    <small class="text-muted">Upload an image file (max 5MB)</small>
                                </div>
                                <div v-if="uploadingLogo" class="mt-2">
                                    <div class="d-flex align-items-center">
                                        <div class="spinner-border spinner-border-sm me-2" role="status">
                                            <span class="visually-hidden">Uploading...</span>
                                        </div>
                                        <small class="text-muted">Uploading logo...</small>
                                    </div>
                                </div>
                            </div>
                            <div class="mb-3">
                                <label class="form-label">Patterns</label>
                                <div class="input-group mb-2" v-for="(pattern, index) in form.patterns" :key="index">
                                    <input type="text" class="form-control" v-model="form.patterns[index]">
                                    <button class="btn btn-danger" type="button" @click="removePattern(index)">
                                        <font-awesome-icon :icon="['fas', 'times']" />
                                    </button>
                                </div>
                                <button type="button" class="btn btn-secondary" @click="addPattern">
                                    <font-awesome-icon :icon="['fas', 'plus']" /> Add Pattern
                                </button>
                            </div>
                            <div class="text-end">
                                <button type="button" class="btn btn-secondary me-2"
                                    data-bs-dismiss="modal">Cancel</button>
                                <button type="submit" class="btn btn-primary" :disabled="savingPattern">
                                    <template v-if="savingPattern">
                                        <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                        Saving...
                                    </template>
                                    <template v-else>
                                        Save
                                    </template>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>

        <!-- Add tooltip -->
        <div
            v-if="tooltip.show"
            class="chart-tooltip"
            :style="{
                left: tooltip.x + 'px',
                top: (tooltip.y - 30) + 'px'
            }"
        >
            {{ tooltip.count }} websites<br>
            {{ tooltip.date }}
        </div>
    </div>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'
import { Modal } from 'bootstrap'
import { handleErrors } from '../../actions/helpers'
import restApi from '../../api'
import LPDataTablePagination from '@/components/lp-data-table/LPDataTablePagination'
import StatsBarChart from '@/components/charts/StatsBarChart'
import { format, getISOWeek, getISOWeekYear, setISOWeek, setISOWeekYear, startOfISOWeek } from 'date-fns'
import { mapState } from 'vuex'

export default {
    name: 'PlatformFingerprints',
    components: {
        PageHeader,
        LPDataTablePagination,
        StatsBarChart
    },
    data() {
        return {
            patterns: {
                data: [],
                pagination: null
            },
            loading: false,
            weeklyChecksLoading: true,
            editMode: false,
            currentId: null,
            $modal: null,
            form: {
                name: '',
                type: 'technology',
                patterns: [],
                color: '#4285f4',
                logo: ''
            },
            tooltip: {
                show: false,
                x: 0,
                y: 0,
                date: '',
                count: 0
            },
            savingPattern: false,
            uploadingLogo: false,
            pendingLogoFile: null,
            chartLoading: true,
            fingerprintCountsChart: {
                labels: [],
                datasets: [
                    {
                        label: 'Municipality Count',
                        backgroundColor: [
                            '#4285f4', '#34a853', '#fbbc05', '#ea4335', '#9c27b0',
                            '#ff9800', '#795548', '#607d8b', '#e91e63', '#00bcd4'
                        ],
                        borderColor: '#ffffff',
                        borderWidth: 2,
                        data: []
                    }
                ]
            },
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            title: (context) => {
                                const dataIndex = context[0].dataIndex
                                const item = this.chartDataItems[dataIndex]
                                return item ? item.name : context[0].label
                            },
                            label: (context) => {
                                const labels = [
                                    `${context.parsed.y.toLocaleString()} municipalities`
                                ]
                                return labels
                            },
                            labelColor: (context) => {
                                const dataIndex = context.dataIndex
                                const item = this.chartDataItems[dataIndex]
                                return {
                                    borderColor: item?.color || '#4285f4',
                                    backgroundColor: item?.color || '#4285f4'
                                }
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        grid: {
                            display: false
                        },
                        ticks: {
                            maxRotation: 45,
                            minRotation: 0
                        }
                    },
                    y: {
                        beginAtZero: true,
                        ticks: {
                            callback: function(value) {
                                return value.toLocaleString()
                            }
                        }
                    }
                }
            },
            chartSelectedType: '',
            selectedWeek: '',
            chartDataItems: [],
            hiddenDatasets: [],
            sortColumn: null,
            sortDirection: 'asc',
        }
    },
    computed: {
        ...mapState(['storageUrl']),
        selectedType: {
            get() {
                return this.$route.query.type || ''
            },
            set(value) {
                const query = { 
                    ...this.$route.query,
                    perPage: this.$route.query.perPage || 40
                }
                if (value) {
                    query.type = value
                } else {
                    delete query.type
                }
                this.$router.push({ query })
            }
        },
        selectedDate: {
            get() {
                if (!this.selectedWeek) return ''
                const [year, week] = this.selectedWeek.split('-W')
                if (!year || !week) return ''
                const tempDate = setISOWeekYear(new Date(), parseInt(year))
                const mondayOfWeek = setISOWeek(tempDate, parseInt(week))
                const monday = startOfISOWeek(mondayOfWeek)
                
                return format(monday, 'yyyy-MM-dd')
            },
            set(value) {
                if (!value) return
                const date = new Date(value)
                const year = getISOWeekYear(date)
                const week = getISOWeek(date)
                this.selectedWeek = `${year}-W${week.toString().padStart(2, '0')}`
                this.$nextTick(() => this.fetchWeeklyChecksForPatterns('date'))
            }
        },
        sortedPatterns() {
            if (!this.patterns?.data?.length || !this.sortColumn) return this.patterns?.data || []
            
            return [...this.patterns.data].sort((a, b) => {
                const aVal = a[this.sortColumn] ?? (this.sortColumn === 'website_count' ? 0 : '')
                const bVal = b[this.sortColumn] ?? (this.sortColumn === 'website_count' ? 0 : '')
                
                if (this.sortColumn === 'website_count') {
                    return this.sortDirection === 'asc' ? aVal - bVal : bVal - aVal
                }
                
                const comparison = String(aVal).toLowerCase().localeCompare(String(bVal).toLowerCase())
                return this.sortDirection === 'asc' ? comparison : -comparison
            })
        },
        totalTrackedCount() {
            return (this.patterns?.data || []).reduce((sum, p) => sum + (p.website_count || 0), 0)
        },
    },
    watch: {
        '$route'() {
            // Refetch data when URL changes
            this.fetchPatterns()
        },
        
    },
    created() {
        // Ensure perPage is in URL if not present
        if (!this.$route.query.perPage) {
            const query = { 
                ...this.$route.query, 
                perPage: 40 
            }
            this.$router.replace({ query })
        }
        
        this.fetchPatterns()
    },
    mounted() {
        this.$modal = new Modal(document.getElementById('patternModal'))
        
        // Initialize selectedWeek if not set
        if (!this.selectedWeek) {
            this.selectedWeek = this.getCurrentWeek()
        }
    },
    methods: {

        // Legend Toggle
        toggleDataset(index) {
            const hiddenIndex = this.hiddenDatasets.indexOf(index)
            if (hiddenIndex > -1) {
                this.hiddenDatasets.splice(hiddenIndex, 1)
            } else {
                this.hiddenDatasets.push(index)
            }
            
            // Update chart data by filtering out hidden items
            const visibleData = this.chartDataItems.filter((item, idx) => !this.hiddenDatasets.includes(idx))
            
            // Update chart with filtered data
            this.fingerprintCountsChart.labels = visibleData.map(item => item.name)
            this.fingerprintCountsChart.datasets[0].data = visibleData.map(item => item.count)
            this.fingerprintCountsChart.datasets[0].backgroundColor = visibleData.map(item => item.color || '#4285f4')
            
            // Force chart update
            if (this.$refs.fingerprintChart && this.$refs.fingerprintChart.chart) {
                this.$refs.fingerprintChart.chart.update()
            }
        },

        // Pattern Management
        addPattern() {
            this.form.patterns.push('')
        },
        removePattern(index) {
            this.form.patterns.splice(index, 1)
        },
        changePage(page) {
            if (page >= 1) {
                const query = { 
                    ...this.$route.query, 
                    page,
                    perPage: this.$route.query.perPage || 40
                }
                this.$router.push({ query })
            }
        },

        // API Calls
        async fetchPatterns() {
            this.loading = true;
            try {
                // Simply use route query parameters directly
                const params = {
                    page: this.$route.query.page || 1,
                    perPage: this.$route.query.perPage || 40,
                    ...(this.$route.query.type && { type: this.$route.query.type })
                }
                
                const { data } = await restApi.get('/platform-fingerprints', { params })
                // Initialize weekly_checks as empty array for each pattern
                data.data = (data.data || []).map(pattern => ({
                    ...pattern,
                    weekly_checks: []
                }))
                this.patterns = data;
                // Fetch weekly_checks in the background
                this.fetchWeeklyChecksForPatterns()
            } catch (error) {
                console.error('Error fetching patterns:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: handleErrors(error, ''),
                    timer: 6000
                })
            } finally {
                this.loading = false;
            }
        },

        async fetchWeeklyChecksForPatterns(filterType = null) {
            const patternIds = (this.patterns.data || []).map(p => p.id)
            if (patternIds.length === 0) {
                this.weeklyChecksLoading = false
                this.chartLoading = false
                return
            }
            
            this.weeklyChecksLoading = true
            this.chartLoading = true
            try {
                const params = { 
                    platform_fingerprint_ids: patternIds.join(','),
                    ...(filterType === 'date' && this.selectedDate && { date: this.selectedDate }),
                    ...(filterType === 'type' && this.chartSelectedType && { type: this.chartSelectedType })
                }
                
                const weeklyChecksResp = await restApi.get('/platform-fingerprints/checks', { params })
                
                if (Array.isArray(weeklyChecksResp.data)) {
                    // Process the flat array response
                    // Group checks by platform_fingerprint_id
                    const checksById = {}
                    
                    weeklyChecksResp.data.forEach(item => {
                        const id = item.platform_fingerprint_id
                        if (!checksById[id]) {
                            checksById[id] = []
                        }
                        
                        checksById[id].push({
                            date: item.date,
                            count: item.count
                        })
                    })
                    
                    const mainChartData = []
                    console.log(this.selectedDate, 'gvvvbvbn')
                    const selectedDate = this.selectedDate
                    
                    Object.keys(checksById).forEach(id => {
                        const pattern = this.patterns.data.find(p => p.id === parseInt(id))
                        if (pattern) {
                            const sortedChecks = checksById[id].sort((a, b) => new Date(a.date) - new Date(b.date))
                            if (!filterType) {
                                this.$set(pattern, 'weekly_checks', sortedChecks || [])
                                this.$set(pattern, 'website_count', sortedChecks && sortedChecks.length > 0 ? sortedChecks[sortedChecks.length - 1].count : null)
                            }
                            
                            // Prepare chart data
                            if (sortedChecks && sortedChecks.length > 0 && (!this.chartSelectedType || pattern.type === this.chartSelectedType)) {
                                const selectedDateCheck = sortedChecks.find(check => check.date === selectedDate)
                                if (selectedDateCheck && selectedDateCheck.count > 0) {
                                    mainChartData.push({
                                        name: pattern.name,
                                        count: selectedDateCheck.count,
                                        type: pattern.type,
                                        color: pattern.color || '#4285f4',
                                        logo: pattern.logo
                                    })
                                }
                            }
                        }
                    })
                    
                    if (mainChartData && mainChartData.length > 0) {
                        const sortedChartData = mainChartData.sort((a, b) => b.count - a.count);
                        
                        // Store the full data for tooltip access
                        this.chartDataItems = sortedChartData;
                        
                        this.fingerprintCountsChart.labels = sortedChartData.map(item => item.name);
                        this.fingerprintCountsChart.datasets[0].data = sortedChartData.map(item => item.count);
                        
                        // Use actual pattern colors instead of generic type colors
                        this.fingerprintCountsChart.datasets[0].backgroundColor = sortedChartData.map(item => item.color || '#4285f4');
                        
                        // Reset hidden datasets when new data loads
                        this.hiddenDatasets = [];
                    } else {
                        this.fingerprintCountsChart.labels = [];
                        this.fingerprintCountsChart.datasets[0].data = [];
                        this.chartDataItems = [];
                        this.hiddenDatasets = [];
                    }
                }
            } catch (error) {
                // Optionally handle error, but don't block UI
                console.error('Error fetching weekly checks:', error)
            } finally {
                this.weeklyChecksLoading = false
                this.chartLoading = false
            }
        },

        openAddModal() {
            this.editMode = false
            this.uploadingLogo = false
            this.pendingLogoFile = null
            this.form = {
                name: '',
                type: 'technology',
                patterns: [],
                color: '#4285f4',
                logo: ''
            }
            this.$modal.show()
        },
        editPattern(pattern) {
            this.editMode = true
            this.currentId = pattern.id
            this.uploadingLogo = false
            this.pendingLogoFile = null
            this.form = { 
                ...pattern,
                color: pattern.color || '#4285f4',
                logo: pattern.logo || ''
            }
            this.$modal.show()
        },
        async syncWithCFDataWorker(id, action) {
            try {
                await fetch(`${process.env.VUE_APP_CF_DATA_WORKER_URL}/api`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        type: 'platform_fingerprints',
                        meta: {
                            date: new Date().toISOString().split('T')[0],
                            platform_fingerprint_id: id,
                            action: action
                        }
                    })
                });
            } catch (error) {
                console.error('Error syncing with CF Data Worker:', error);
            }
        },
        async savePattern() {
            this.savingPattern = true;
            try {
                const payload = {
                    name: this.form.name,
                    patterns: this.form.patterns,
                    color: this.form.color,
                    logo: this.form.logo
                }

                if (!this.editMode) {
                    payload.type = this.form.type
                }

                if (this.editMode) {
                    await Promise.race([
                        restApi.put(`/platform-fingerprints/${this.currentId}`, payload),
                        new Promise((_, reject) => 
                            setTimeout(() => reject(new Error('Request timeout')), 30000)
                        )
                    ]);
                } else {
                    const response = await Promise.race([
                        restApi.post('/platform-fingerprints', payload),
                        new Promise((_, reject) => 
                            setTimeout(() => reject(new Error('Request timeout')), 30000)
                        )
                    ]);
                    
                    // Upload pending logo if exists
                    if (this.pendingLogoFile) {
                        await this.performLogoUpload(this.pendingLogoFile, response.data.id)
                    }
                    
                    // Start CF Data Worker sync in background
                    await this.syncWithCFDataWorker(response.data.id, this.editMode ? 'update' : 'create')
                        .catch(error => console.error('Background sync failed:', error));
                }
                
                this.$modal.hide();
                await this.fetchPatterns();
                this.$swal.fire({
                    icon: 'success',
                    title: `Pattern ${this.editMode ? 'updated' : 'added'} successfully!`,
                    timer: 3000
                });
            } catch (error) {
                console.error('Error saving pattern:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: handleErrors(error, 'Request failed or timed out'),
                    timer: 6000
                });
            } finally {
                this.savingPattern = false;
            }
        },
        async deletePattern(id) {
            this.$swal({
                title: "Are you sure?",
                showCancelButton: true,
                icon: "warning",
                text: "Are you sure you want to delete this pattern?",
                confirmButtonText: "Yes, delete it!",
                showLoaderOnConfirm: true,
                toast: false,
                position: 'center',
                showConfirmButton: true,
                timer: undefined,
                timerProgressBar: false,
                confirmButtonColor: "#0FCB4F",
                cancelButtonColor: "#EC2641",
                preConfirm: async () => {
                    try {
                        let res = await restApi.delete(`/platform-fingerprints/${id}`);
                        // Sync delete with CF Data Worker
                        await this.syncWithCFDataWorker(id, 'delete');
                        return res;
                    } catch (error) {
                        this.$swal.showValidationMessage(`Request failed: ${error}`);
                    }
                },
                allowOutsideClick: () => !this.$swal.isLoading()
            }).then(async (result) => {
                if (result.isConfirmed) {
                    this.$swal.fire({
                        icon: 'success',
                        title: 'Pattern deleted successfully!',
                        timer: 3000
                    });
                    await this.fetchPatterns();
                }
            });
        },

        minCheck(weeklyChecks) {
            return Math.min(...weeklyChecks.map(d => d.count));
        },
        maxCheck(weeklyChecks) {
            return Math.max(...weeklyChecks.map(d => d.count));
        },
        getBarHeight(weeklyChecks, count) {
            const min = this.minCheck(weeklyChecks);
            const max = this.maxCheck(weeklyChecks);
            if (max === min) return 8;
            const range = max - min;
            const normalizedHeight = (count - min) / range;
            const minHeight = 7;
            const maxHeight = 14;
            const heightRange = maxHeight - minHeight;
            const smoothedHeight = Math.pow(normalizedHeight, 0.7);
            return minHeight + (smoothedHeight * heightRange);
        },
        getBarColor(weeklyChecks, index) {
            const current = weeklyChecks[index].count;
            const max = this.maxCheck(weeklyChecks);
            const min = this.minCheck(weeklyChecks);
            if (max === min) return '#4285f4';
            const normalized = (current - min) / (max - min);
            if (normalized === 1) return '#28a745';
            if (normalized === 0) return '#dc3545';
            return '#4285f4';
        },

        showTooltip(event, weeklyChecks) {
            if (!weeklyChecks || !weeklyChecks.length) return;
            
            const svg = event.currentTarget;
            const rect = svg.getBoundingClientRect();
            const x = event.clientX - rect.left;
            
            // Calculate which data point we're closest to based on new bar positioning
            // const width = 100;
            const padding = 2;
            const effectiveWidth = 92;
            const barWidth = effectiveWidth / weeklyChecks.length;
            
            // Find the closest bar based on x position
            const index = Math.min(
                Math.floor((x - padding) / barWidth),
                weeklyChecks.length - 1
            );
            
            if (index >= 0 && index < weeklyChecks.length) {
                const data = weeklyChecks[index];
                this.tooltip = {
                    show: true,
                    x: event.clientX,
                    y: event.clientY,
                    date: new Date(data.date).toLocaleDateString(),
                    count: data.count.toLocaleString()
                };
            }
        },

        hideTooltip() {
            this.tooltip.show = false;
        },

        // Week picker methods
        getCurrentWeek() {
            const now = new Date()
            const year = now.getFullYear()
            const week = this.getWeekNumber(now)
            return `${year}-W${week.toString().padStart(2, '0')}`
        },
        
        getWeekNumber(date) {
            const firstDayOfYear = new Date(date.getFullYear(), 0, 1)
            const pastDaysOfYear = (date - firstDayOfYear) / 86400000
            return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7)
        },
        
        // Sorting methods
        sortBy(column) {
            this.sortDirection = this.sortColumn === column && this.sortDirection === 'asc' ? 'desc' : 'asc'
            this.sortColumn = column
        },
        getSortIcon(column) {
            if (this.sortColumn !== column) return 'fa-sort text-muted'
            return this.sortDirection === 'asc' ? 'fa-sort-up text-primary' : 'fa-sort-down text-primary'
        },

         // Logo upload methods
         uploadLogo(event) {
             const file = event.target.files[0]
             if (!file) return
 
             // Validate file
             if (!file.type.startsWith('image/')) return this.$swal.fire({ icon: 'error', title: 'Invalid file type', text: 'Please select an image file', timer: 3000 })
             if (file.size > 5 * 1024 * 1024) return this.$swal.fire({ icon: 'error', title: 'File too large', text: 'Please select an image smaller than 5MB', timer: 3000 })
 
             // For new patterns, store file and show preview. For existing, upload immediately
             if (!this.editMode) {
                 this.pendingLogoFile = file
                 this.form.logo = URL.createObjectURL(file)
             } else {
                 this.performLogoUpload(file, this.currentId)
             }
         },

         performLogoUpload(file, patternId) {
             this.uploadingLogo = true
             const formData = new FormData()
             formData.append('logo', file)
             formData.append('id', patternId)

             restApi.post('/platform-fingerprints/upload-logo', formData, { headers: { 'content-type': 'multipart/form-data' } })
                 .then(({ data }) => {
                     this.form.logo = data
                     this.pendingLogoFile = null
                     if (this.$refs.logoFileInput) this.$refs.logoFileInput.value = ''
                     this.$swal.fire({ icon: 'success', title: 'Logo uploaded successfully!', timer: 3000 })
                 })
                 .catch(error => {
                     console.error('Logo upload error:', error)
                     if (this.$refs.logoFileInput) this.$refs.logoFileInput.value = ''
                     this.$swal.fire({ icon: 'error', title: handleErrors(error, 'logoUpload'), timer: 6000 })
                 })
                 .finally(() => this.uploadingLogo = false)
         },

        
    }
}
</script>

<style scoped>
.table td {
    vertical-align: middle;
}

.table.loading {
    opacity: 0.6;
    pointer-events: none;
}

.chart-cell {
    width: 100px;
    padding: 8px !important;
    vertical-align: middle;
}

.chart-container {
    width: 100px;
    height: 20px;
    position: relative;
}

.trend-line {
    display: block;
    width: 100%;
    height: 100%;
}

.chart-tooltip {
    position: fixed;
    z-index: 9999;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    pointer-events: none;
    transform: translateX(-50%);
    white-space: nowrap;
    text-align: center;
}

.chart-loader {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 20px;
}

.spinner-border-sm {
    width: 1rem;
    height: 1rem;
    border-width: 0.1em;
}

.empty-chart-text {
    font-size: 10px;
    fill: #aaa;
    font-family: sans-serif;
}

.sortable-header {
    cursor: pointer;
    user-select: none;
}

.sortable-header:hover {
    background-color: rgba(0, 0, 0, 0.05);
}

</style>